import { FC, useCallback, useMemo } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  lighten,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShortcutIcon from "@mui/icons-material/Shortcut";
import {
  DataGridPro as DataGrid,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridColDef,
  GridRowClassNameParams,
  GridRowParams,
} from "@mui/x-data-grid-pro";
import moment from "moment";
import { uniqBy } from "lodash";

import translateLabels from "src/utils/Translations/translateLabels";
import __ from "src/utils/Translations";

import { useFetchSubscriptionPaymentsPeriods } from "./query";
import { EventLogsPaymentPeriodWithOperation } from "./models";
import {
  financingSourceTypeOptions,
  onlinePaymentMethodTypeOptions,
  operationStatusOptions,
  operationTypeOptions,
  paymentMethodTypeOptions,
  periodStatusOptions,
} from "./constans";

interface Props {
  subscriptionId: string;
}

const DetailPanelExpandIcon = () => (
  <IconButton size="small">
    <ExpandMoreIcon />
  </IconButton>
);

const DetailPanelCollapseIcon = () => (
  <IconButton size="small">
    <ExpandLessIcon />
  </IconButton>
);

const getDetailPanelContent = ({
  row,
}: GridRowParams<EventLogsPaymentPeriodWithOperation>) => {
  const { operation } = row;
  const paymentUri = operation.onlinePaymentOrders?.find(
    (o) => o.status === "ORDER_PENDING"
  )?.paymentUri;
  const orderId =
    (operation.type === "PAYMENT" &&
      operation.financingSource?.type === "ONLINE_PAYMENT" &&
      operation.lastOnlinePaymentOrder?.orderId) ||
    operation.id;

  return (
    <Grid container>
      <Grid size={6}>
        <List dense>
          <ListItem key="lastOnlinePaymentOrder.orderId">
            <ListItemText primary={__("Id zamówienia")} secondary={orderId} />
          </ListItem>
          <ListItem key="financingSource.type">
            <ListItemText
              primary={__("Źródło płatności")}
              secondary={
                operation.financingSource?.name ||
                financingSourceTypeOptions.find(
                  (o) => o.value === operation.financingSource?.type
                )?.label
              }
            />
          </ListItem>
          <ListItem key="financingSource.onlinePaymentDetails.methodType">
            <ListItemText
              primary={__("Metoda płatności")}
              secondary={
                __(
                  onlinePaymentMethodTypeOptions.find(
                    (o) =>
                      o.value ===
                      operation.financingSource?.onlinePaymentDetails
                        ?.methodType
                  )?.label ||
                    operation.financingSource?.onlinePaymentDetails?.methodType
                ) || __("Nieokreślono")
              }
            />
          </ListItem>
          <ListItem key="cost">
            <ListItemText
              primary={__("Kwota")}
              secondary={Number(operation.cost).toFixed(2)}
            />
          </ListItem>
          <ListItem key="period">
            <ListItemText
              primary={__("Okres")}
              secondary={`${__("od")} ${operation.period?.from} ${__("do")} ${
                operation.period?.to
              }`}
            />
          </ListItem>
        </List>
      </Grid>
      <Grid size={6}>
        <List dense>
          <ListItem key="status">
            <ListItemText
              primary={__("Status")}
              secondary={
                __(
                  operationStatusOptions.find(
                    (o) => o.value === operation.status
                  )?.label
                ) || operation.status
              }
            />
          </ListItem>
          <ListItem key="completedAt">
            <ListItemText
              primary={__("Data wykonania transakcji")}
              secondary={
                (operation.completedAt &&
                  moment(operation.completedAt).format(
                    "YYYY-MM-DD HH:mm:ss"
                  )) ||
                __("Nieokreślono")
              }
            />
          </ListItem>
          <ListItem key="createdAt">
            <ListItemText
              primary={__("Data utworzenia płatności przez system")}
              secondary={
                operation.createdAt &&
                moment(operation.createdAt).format("YYYY-MM-DD HH:mm:ss")
              }
            />
          </ListItem>
          <ListItem key="latestOnlinePaymentRefund.executeAt">
            <ListItemText
              primary={__("Data wyjścia zwrotu płatności do PayU")}
              secondary={
                (operation.latestOnlinePaymentRefund &&
                  moment(operation.latestOnlinePaymentRefund.executeAt).format(
                    "YYYY-MM-DD HH:mm:ss"
                  )) ||
                __("Nieokreślono")
              }
            />
          </ListItem>
          {paymentUri && (
            <ListItem key="operation.onlinePaymentOrders[ORDER_PENDING].paymentUri">
              <Link href={paymentUri} target="_blank" rel="noreferrer">
                {__("Link do aktywnej płatności")} <ShortcutIcon />
              </Link>
            </ListItem>
          )}
        </List>
      </Grid>
    </Grid>
  );
};

const SubscriptionBenefitsPaymentsPeriods: FC<Props> = ({ subscriptionId }) => {
  const { data, isFetching, isError, error } =
    useFetchSubscriptionPaymentsPeriods(
      `subscriptionId=="${subscriptionId}"`,
      1,
      100,
      []
    );

  const columns = useMemo(
    () =>
      [
        {
          headerName: __("Okres"),
          field: "period",
          type: "string",
          width: 100,
        },
        {
          headerName: __("Status"),
          field: "status",
          type: "singleSelect",
          valueOptions: translateLabels(periodStatusOptions),
          rowSpanValueGetter: (value, row) =>
            row ? `${row.period}-${row.status}` : value,
          width: 100,
        },
        {
          headerName: __("Typ transakcji"),
          field: "operation.type",
          type: "singleSelect",
          valueGetter: (_value, row) => row.operation.type,
          valueOptions: translateLabels(operationTypeOptions),
          rowSpanValueGetter: () => null,
          width: 120,
        },
        {
          headerName: __("Źódło finansowania"),
          field: "operation.financingSource.type",
          type: "singleSelect",
          valueGetter: (_value, row) => row.operation.financingSource.type,
          valueOptions: translateLabels(financingSourceTypeOptions),
          rowSpanValueGetter: () => null,
          width: 200,
        },
        {
          headerName: __("Sposób opłacania"),
          field: "operation.financingSource.paymentMethodType",
          type: "singleSelect",
          valueGetter: (_value, row) =>
            row.operation.financingSource.paymentMethodType,
          valueOptions: translateLabels(paymentMethodTypeOptions),
          rowSpanValueGetter: () => null,
          width: 180,
        },
        {
          headerName: __("Kwota transakcji"),
          field: "operation.cost",
          type: "number",
          valueGetter: (_value, row) => Number(row.operation.cost).toFixed(2),
          rowSpanValueGetter: () => null,
          width: 150,
        },
        {
          headerName: __("Status transakcji"),
          field: "operation.status",
          type: "singleSelect",
          valueGetter: (_value, row) => row.operation.status,
          valueOptions: translateLabels(operationStatusOptions),
          rowSpanValueGetter: () => null,
          width: 150,
        },
        {
          headerName: __("Data wykonania transakcji"),
          field: "operation.createdAt",
          type: "date",
          valueGetter: (_value, row) =>
            row.operation.createdAt && new Date(row.operation.createdAt),
          valueFormatter: (value) =>
            value && new Date(value).toISOString().slice(0, 10),
          rowSpanValueGetter: () => null,
          flex: 2,
        },
        {
          ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
          width: 60,
        },
      ] as GridColDef<EventLogsPaymentPeriodWithOperation>[],
    []
  );

  const rows = useMemo(
    () =>
      data?.content.reduce((rows, item) => {
        uniqBy(item.operations, "id").forEach((operation) => {
          rows.push({
            id: item.id,
            period: item.period,
            status: item.status,
            operation,
          });
        });
        return rows;
      }, [] as EventLogsPaymentPeriodWithOperation[]) || [],

    [data?.content]
  );

  const getDetailPanelHeight = useCallback(() => "auto", []);

  const getRowClassName = useCallback(
    ({ row }: GridRowClassNameParams<EventLogsPaymentPeriodWithOperation>) =>
      `event-logs-period-status--${row.status}`,
    []
  );

  return (
    <>
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ margin: "1rem 0 1rem" }}>
            {__("Lista transakcji")}
          </Typography>
          {isError && (
            <Alert variant="outlined" color="warning">
              {String(error)}
            </Alert>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 100,
            }}
          >
            <DataGrid
              columns={columns}
              getRowId={(row) => row.operation.id}
              rows={rows}
              loading={isFetching}
              initialState={{
                pinnedColumns: {
                  left: [],
                  right: [GRID_DETAIL_PANEL_TOGGLE_FIELD],
                },
                sorting: {
                  sortModel: [{ field: "period", sort: "desc" }],
                },
              }}
              density="compact"
              disableColumnPinning
              disableColumnReorder
              disableColumnSelector
              disableRowSelectionOnClick
              disableVirtualization
              disableEval
              rowSpanning
              showCellVerticalBorder={false}
              showColumnVerticalBorder
              slots={{
                detailPanelExpandIcon: DetailPanelExpandIcon,
                detailPanelCollapseIcon: DetailPanelCollapseIcon,
              }}
              slotProps={{
                loadingOverlay: {
                  variant: "linear-progress",
                  noRowsVariant: "circular-progress",
                },
              }}
              getDetailPanelContent={getDetailPanelContent}
              getDetailPanelHeight={getDetailPanelHeight}
              getRowClassName={getRowClassName}
              sx={(theme) => ({
                maxHeight: "400px",
                "& .event-logs-period-status--PAID": {
                  backgroundColor: "#9cf180",
                  "&:hover": {
                    backgroundColor: lighten("#9cf180", 0.6),
                  },
                },
                "& .event-logs-period-status--ADVANCE, & .event-logs-period-status--UNPAID":
                  {
                    backgroundColor: "#f6f483",
                    "&:hover": {
                      backgroundColor: lighten("#f6f483", 0.6),
                    },
                  },
                "& .event-logs-period-status--REFUNDED, & .event-logs-period-status--CANCELED":
                  {
                    backgroundColor: theme.palette.deepOrange[300],
                    "&:hover": {
                      backgroundColor: lighten(
                        theme.palette.deepOrange[300],
                        0.6
                      ),
                    },
                  },
                "& .MuiDataGrid-footerContainer": {
                  minHeight: "initial",
                },
                "& .MuiDataGrid-scrollbar--vertical": {
                  backgroundColor: "#fff !important",
                },
              })}
            />
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default SubscriptionBenefitsPaymentsPeriods;
